// Imports the platform-neutral values file directly: this generator needs the
// raw hex, and `roleTokenValues.ts` is guaranteed never to grow a `.web.ts`
// sibling that Metro would resolve instead.
import {
  cssVarName,
  roleColorValues as roleColors,
  type RoleToken,
} from './roleTokenValues';

/**
 * Builds the `:root` / `.dark` custom-property block that backs the web
 * theme. This is the single generator for those declarations, so the CSS can
 * never disagree with the TypeScript token maps.
 *
 * The output is inlined into `apps/gripp-web/public/index.html` rather than
 * imported by the bundle, because it has to resolve before first paint. A
 * stylesheet that arrives with the JS would let the page render light and
 * then snap to dark, which is the single most visible way this feature can
 * look broken.
 *
 * Regenerate with `npm run theme:css` from the workspace root; the paired
 * spec fails if the committed HTML drifts from these tokens.
 */

// CSS comments, not HTML ones: `<!--` inside <style> is a legacy CDO token
// that parsers ignore rather than reject, which works but reads like a bug.
export const THEME_CSS_START = '/* theme-tokens:start */';
export const THEME_CSS_END = '/* theme-tokens:end */';

const declarations = (mode: 'light' | 'dark', indent: string): string =>
  (Object.keys(roleColors[mode]) as RoleToken[])
    .map((token) => `${indent}${cssVarName(token)}: ${roleColors[mode][token]};`)
    .join('\n');

export const buildThemeCss = (indent = '      '): string => {
  const inner = `${indent}  `;
  return [
    `${indent}/* Generated by libs/shared-ui/src/themes/themeCss.ts — do not hand-edit.`,
    `${indent}   Run \`npm run theme:css\` after changing role tokens. */`,
    `${indent}:root {`,
    declarations('light', inner),
    `${indent}}`,
    `${indent}:root.dark {`,
    declarations('dark', inner),
    `${indent}}`,
  ].join('\n');
};

/** Replaces the marked block in an index.html string. Throws if absent. */
export const injectThemeCss = (html: string): string => {
  const start = html.indexOf(THEME_CSS_START);
  const end = html.indexOf(THEME_CSS_END);
  if (start === -1 || end === -1) {
    throw new Error(
      `index.html is missing ${THEME_CSS_START} / ${THEME_CSS_END} markers`,
    );
  }
  const indentMatch = /\n([ \t]*)$/.exec(html.slice(0, start));
  const indent = indentMatch ? indentMatch[1] : '      ';
  return [
    html.slice(0, start + THEME_CSS_START.length),
    '\n',
    buildThemeCss(indent),
    '\n',
    indent,
    html.slice(end),
  ].join('');
};

/** Parses `--token: value;` pairs out of a generated block, for the drift spec. */
export const parseThemeCss = (
  css: string,
): { light: Record<string, string>; dark: Record<string, string> } => {
  const grab = (selector: string): Record<string, string> => {
    const body = new RegExp(`${selector}\\s*\\{([^}]*)\\}`).exec(css)?.[1] ?? '';
    return Object.fromEntries(
      [...body.matchAll(/(--[a-z0-9-]+)\s*:\s*([^;]+);/gi)].map((m) => [
        m[1],
        m[2].trim(),
      ]),
    );
  };
  return { light: grab(':root(?!\\.)'), dark: grab(':root\\.dark') };
};
